import React, {useState} from 'react';
import env from 'env';
import styled from 'styled-components';
import {detectMobileDevice} from 'helpers/checkIsNativeDevice';
import IMAGES from 'constants/Images';
import {QRCodeSVG} from 'qrcode.react';
import useInterval from 'hooks/useInterval';
import {checkRPDStatus, mockRPDStatus, generateRPDRequest} from 'api/rest';
import {BANK_VERIFICATION_TYPE, KYC_FIREBASE_EVENTS} from 'constants/kycFirebaseEvents';
import {handleApiError, triggerToast} from 'helpers';
import {logKycFirebaseEvent} from 'utils/logFirebaseEvents';
import {ActionButton, Spinner} from 'screens/commons';

const RPD_STATUS = {
  BAV_REVERSE_PENNY_DROP_CREATED: 'BAV_REVERSE_PENNY_DROP_CREATED',
  BAV_REVERSE_PENNY_DROP_FAILED: 'BAV_REVERSE_PENNY_DROP_FAILED',
  BAV_REVERSE_PENNY_DROP_EXPIRED: 'BAV_REVERSE_PENNY_DROP_EXPIRED',
  BAV_REVERSE_PENNY_DROP_PAYMENT_SUCCESSFUL: 'BAV_REVERSE_PENNY_DROP_PAYMENT_SUCCESSFUL',
  BAV_REVERSE_PENNY_DROP_PAYMENT_FAILED: 'BAV_REVERSE_PENNY_DROP_PAYMENT_FAILED',
};

const transformUPILinkForIOS = (upiUrl) => {
  if (!upiUrl) return {};

  const deeplinks = {
    phonepe: upiUrl.replace('upi://', 'phonepe://'),
    gpay: upiUrl.replace('upi://', 'tez://upi/'),
    paytm: upiUrl.replace('upi://', 'paytmmp://'),
    bharatpe: upiUrl.replace('upi://', 'bharatpe://'),
  };
  return deeplinks;
};

const getUPIRedirectLink = (upiLink, app) => {
  if (!upiLink) return '';
  if (app === 'upi') return upiLink;

  const deeplinks = transformUPILinkForIOS(upiLink);
  return deeplinks[app] || '';
};

function RPD({kycData, updateStatus}) {
  const {isMobile, isAndroid} = detectMobileDevice();
  const [rpdDetails, setRPDDetails] = useState(null);
  const [requestDetails, setRequestDetails] = useState(null);
  const [isGeneratingRPD, setIsGeneratingRPD] = useState(false);

  const [isSkippingRPD, setIsSkippingRPD] = useState(false);

  const [isMocking, setIsMocking] = useState(false);

  const mockStatus = (status) => {
    setIsMocking(true);
    mockRPDStatus(rpdDetails.id, status)
      .then(() => {
        triggerToast('Mock Success', 'success');
      })
      .catch((e) => {
        triggerToast('Mock failed');
      })
      .finally(() => {
        setIsMocking(false);
      });
  };

  const checkRequestStatus = () => {
    if (!rpdDetails?.id) return;

    checkRPDStatus(rpdDetails.id)
      .then((res) => {
        setRequestDetails(res.data);

        switch (res.data.status) {
          case RPD_STATUS.BAV_REVERSE_PENNY_DROP_EXPIRED:
            triggerToast('Current Penny drop request expired', 'info');
            break;
          case RPD_STATUS.BAV_REVERSE_PENNY_DROP_FAILED:
            triggerToast(
              'Penny drop verification failed. Proceeding to alternative method',
              'info'
            );
            break;
          case RPD_STATUS.BAV_REVERSE_PENNY_DROP_PAYMENT_SUCCESSFUL:
            break;
          case RPD_STATUS.BAV_REVERSE_PENNY_DROP_PAYMENT_FAILED:
            triggerToast('Payment failed. Proceeding to alternative method', 'info');
            break;
          case RPD_STATUS.BAV_REVERSE_PENNY_DROP_CREATED:
          default:
            // Initial state, no toast needed
            break;
        }
        if (res?.data?.status === RPD_STATUS.BAV_REVERSE_PENNY_DROP_PAYMENT_SUCCESSFUL) {
          logKycFirebaseEvent(kycData?.flow, KYC_FIREBASE_EVENTS.BANK_VERIFIED, {
            verificationType: BANK_VERIFICATION_TYPE.UPI,
          });
        }
        if (res?.data?.status !== RPD_STATUS.BAV_REVERSE_PENNY_DROP_CREATED) updateStatus();
      })
      .catch((e) => {
        triggerToast('Payment failed. Proceeding to alternative method', 'info');
        updateStatus();
      });
  };

  useInterval(checkRequestStatus, 3500);
  const isRPDSuccess =
    requestDetails?.status === RPD_STATUS.BAV_REVERSE_PENNY_DROP_PAYMENT_SUCCESSFUL;

  const fetchRPDDetails = async (onSuccess) => {
    if (isGeneratingRPD) return;

    setIsGeneratingRPD(true);
    try {
      const res = await generateRPDRequest();
      const rpdDetails = res?.data?.rpdDetails;

      setRPDDetails(rpdDetails);
      onSuccess?.(rpdDetails);
    } catch (err) {
      handleApiError(err);
    } finally {
      setIsGeneratingRPD(false);
    }
  };

  const showQRCode = () => {
    fetchRPDDetails();
  };

  const openUPIApp = (app) => {
    if (!isMobile) return;

    fetchRPDDetails((rpdDetails) => {
      const redirectUrl = getUPIRedirectLink(rpdDetails?.upiLink, app);

      if (!redirectUrl) {
        triggerToast('Unable to open UPI app right now. Please try again.');
        return;
      }

      window.location.href = redirectUrl;
    });
  };

  const enterDetailsManually = () => {
    setIsSkippingRPD(true);

    generateRPDRequest({skipRpd: true})
      .then((res) => {
        updateStatus();
      })
      .catch((e) => {
        handleApiError(e);
      })
      .finally(() => {
        setIsSkippingRPD(false);
      });
  };

  const renderUPIDeepLinks = () => (
    <IosLinksContainer>
      <Heading className="text-start w-100">Verify Via:</Heading>
      <IosLinks>
        <a
          className={`upi-link-code ${isGeneratingRPD ? 'disabled' : ''}`}
          href="#"
          onClick={(e) => {
            e.preventDefault();
            openUPIApp('phonepe');
          }}
        >
          <UpiAppIcon className="upi-logo" src={IMAGES.phonePe} />
          PhonePe
          <ArrowIcon src={IMAGES.arrowHeadRight} />
        </a>
        <a
          className={`upi-link-code ${isGeneratingRPD ? 'disabled' : ''}`}
          href="#"
          onClick={(e) => {
            e.preventDefault();
            openUPIApp('gpay');
          }}
        >
          <UpiAppIcon className="upi-logo" src={IMAGES.gpay} />
          Google Pay
          <ArrowIcon src={IMAGES.arrowHeadRight} />
        </a>
        <a
          className={`upi-link-code ${isGeneratingRPD ? 'disabled' : ''}`}
          href="#"
          onClick={(e) => {
            e.preventDefault();
            openUPIApp('paytm');
          }}
        >
          <UpiAppIcon className="upi-logo" src={IMAGES.paytm} />
          Paytm
          <ArrowIcon src={IMAGES.arrowHeadRight} />
        </a>
        {isAndroid ? (
          <a
            className={`upi-link-code ${isGeneratingRPD ? 'disabled' : ''}`}
            href="#"
            onClick={(e) => {
              e.preventDefault();
              openUPIApp('upi');
            }}
          >
            <UpiAppIcon className="upi-logo" src={IMAGES.upiLogo} />
            Any UPI app
            <ArrowIcon src={IMAGES.arrowHeadRight} />
          </a>
        ) : null}
      </IosLinks>
      {isGeneratingRPD ? (
        <BackDropBlurEffect role="status" aria-live="polite" aria-busy="true">
          <Spinner color="purple" size={32} />
        </BackDropBlurEffect>
      ) : null}
    </IosLinksContainer>
  );

  const renderAddDetailsManually = () => (
    <>
      <InfoText className="manual-selection-or-txt">— OR —</InfoText>
      <InfoText className="manual-enter-text">Don't have access to UPI apps?</InfoText>
      <ActionButton
        buttonText={'Verify Bank Details Manually →'}
        callback={enterDetailsManually}
        isLoading={isSkippingRPD}
        isDisabled={isSkippingRPD}
        className="enter-manually-btn body-regular"
        spinnerColor="blue"
      />
    </>
  );

  const renderQRInDesktop = () => (
    <DesktopQRCode>
      <P className="sub-text mb-0 body-medium">Scan QR code with any UPI app</P>
      {renderInfoText()}
      <UpiIcons>
        <UpiAppIcon className="upi-logo" src={IMAGES.phonePe} />
        <UpiAppIcon className="upi-logo" src={IMAGES.gpay} />
        <UpiAppIcon className="upi-logo upi-logo-paytm" src={IMAGES.paytm} />
        <UpiAppIcon className="upi-logo" src={IMAGES.bharathPay} />
        <UpiAppIcon className="upi-logo upi-logo-whatsapp" src={IMAGES.whatsapp} />
      </UpiIcons>
      <QRCodeContainer>
        {!rpdDetails ? (
          <QRCodeOverlay>
            <ActionButton
              buttonText="Show QR code"
              callback={showQRCode}
              isLoading={isGeneratingRPD}
              isDisabled={isGeneratingRPD}
              spinnerColor="white"
              additionalStyles={{backgroundColor: '#6725F4'}}
            />
          </QRCodeOverlay>
        ) : null}
        <QRCodeSVG
          className={`desktop-qr-code ${!rpdDetails ? 'blurred' : ''}`}
          value={rpdDetails?.upiLink || 'https://wealthydev.in/'}
          size={256}
          fgColor={'#421a99'}
        />
      </QRCodeContainer>
    </DesktopQRCode>
  );

  const renderRPDQRAndLinks = () => {
    if (isMobile)
      return (
        <>
          {renderUPIDeepLinks()}
          {renderAddDetailsManually()}
        </>
      );
    return (
      <>
        {renderQRInDesktop()}
        {renderAddDetailsManually()}
      </>
    );
  };

  const renderInfoText = () => (
    <InfoText className="body-regular mt-1">
      Pay ₹1 from your account for verification, it will be refunded within 3 working days
    </InfoText>
  );

  const renderMockBtns = () => (
    <MockPaymentBtns>
      {isMocking ? <Spinner color="blue" /> : null}
      <MockBtn className="mock-success" onClick={() => mockStatus('successful')}>
        Mock Payment Success
      </MockBtn>
      <MockBtn className="mock-failure" onClick={() => mockStatus('expired')}>
        Mock Payment Failure
      </MockBtn>
    </MockPaymentBtns>
  );

  return (
    <Wrapper>
      <CardHeader>{isMobile ? renderInfoText() : null}</CardHeader>
      <CardContent>
        {env.IS_PRODUCTION || !rpdDetails ? null : renderMockBtns()}
        {!isRPDSuccess ? renderRPDQRAndLinks() : null}
        {isRPDSuccess ? (
          <SuccessWrapper>
            <SuccessText>
              Payment done!{' '}
              <span role="img" aria-label="check">
                ✅
              </span>
            </SuccessText>
            <Amount>₹1.00</Amount>
            <VerificationText>Bank Account Verification</VerificationText>
          </SuccessWrapper>
        ) : null}
      </CardContent>
    </Wrapper>
  );
}

export default RPD;

const InfoText = styled.p`
  margin: 1rem 0;
  color: ${(props) => props.theme.colors.text.secondary || '#64748b'};
`;

const Wrapper = styled.div`
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;

  .enter-manually-btn {
    background: #fff;
    color: ${(props) => props.theme.colors.brand.primary};
    margin: 0;
    width: max-content;
    border: 1px solid ${(props) => props.theme.colors.brand.primary};
    padding: 1rem 1.5rem;
    display: flex;
    justify-content: center;
    align-items: center;
    min-width: 250px;
  }
  .manual-selection-or-txt {
    margin: 0.5rem 0;
    font-size: 16px;
  }

  .enter-manually-btn {
    img {
      color: ${(props) => props.theme.colors.brand.primary};
    }
  }
`;

const CardHeader = styled.div`
  padding: 0.5rem 1rem;
`;

const CardContent = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 1rem;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${(props) => props.theme.colors.background.secondary || '#f1f5f9'};
    border-radius: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${(props) => props.theme.colors.brand.primary || '#7c3aed'};
    border-radius: 8px;
  }

  @media screen and (max-width: 768px) {
    padding-bottom: 4rem;
    width: 100%;
  }
`;

const Heading = styled.h1`
  font-size: 18px;
  font-weight: 500;
  text-align: center;
  color: ${(props) => props.theme.colors.text.primary || '#1e293b'};
`;

const IosLinks = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: start;
  align-items: center;
  width: 100%;

  .upi-link-code {
    margin: 12px 0;
    font-size: 16px;
    text-decoration: none;
    color: ${(props) => props.theme.colors.text.primary || '#1e293b'};
    padding: 12px 20px;
    background: ${(props) => props.theme.colors.background.primary || '#ffffff'};
    border-radius: ${(props) => props.theme.borderRadius.md || '0.375rem'};
    border: 1px solid ${(props) => props.theme.colors.border?.default || '#e2e8f0'};
    flex-grow: 1;
    width: 100%;
    display: flex;
    justify-content: start;
    align-items: center;
    transition: all 0.15s ease;

    &:hover {
      transform: translateY(-2px);
      border-color: ${(props) => props.theme.colors.brand.primary || '#7c3aed'};
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    }

    &.disabled {
      pointer-events: none;
      opacity: 0.7;
    }
  }
`;

const UpiAppIcon = styled.img`
  height: 32px;
  width: 32px;
  margin-right: 16px;
`;

const P = styled.p`
  color: ${(props) => props.theme.colors.text.primary || '#1e293b'};
`;

const UpiIcons = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0.5rem 0 1.5rem 0;
  width: 100%;

  .upi-logo {
    height: 32px;
    width: 32px;
    margin-right: 16px;
  }

  .upi-logo-paytm {
    height: 38px;
    width: 60px;
  }

  .upi-logo-whatsapp {
    width: 52px;
    height: 40px;
    margin-right: 0;
  }
`;

const DesktopQRCode = styled.div`
  margin: 1rem 0;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  width: 100%;

  .desktop-qr-code {
    border: 1px solid ${(props) => props.theme.colors.brand.primary || '#7c3aed'};
    padding: 1rem;
    border-radius: ${(props) => props.theme.borderRadius.md || '0.375rem'};
  }

  .desktop-qr-code.blurred {
    filter: blur(6px);
    opacity: 0.75;
    pointer-events: none;
  }
`;

const QRCodeContainer = styled.div`
  position: relative;
`;

const QRCodeOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;

  button {
    min-width: 180px;
  }
`;

const SuccessWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px;
  margin-top: 20px;
  text-align: center;
  width: 100%;
  background-color: ${(props) => props.theme.colors.background.secondary || '#f1f5f9'};
  border-radius: ${(props) => props.theme.borderRadius.md || '0.375rem'};
`;

const SuccessText = styled.h2`
  color: #2e7d32;
  font-size: 24px;
  margin-bottom: 16px;
`;

const Amount = styled.div`
  font-size: 32px;
  font-weight: bold;
  margin-bottom: 8px;
  color: ${(props) => props.theme.colors.text.primary || '#1e293b'};
`;

const VerificationText = styled.div`
  font-size: 16px;
  color: ${(props) => props.theme.colors.text.secondary || '#64748b'};
  margin-bottom: 16px;
`;
const IosLinksContainer = styled.div`
  position: relative;
  width: 100%;
  padding: 0 16px;
`;

const BackDropBlurEffect = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.375rem;
  background: rgba(255, 255, 255, 0.35);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);

  .rpd-upi-generating-spinner {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const MockBtn = styled.button`
  padding: 0.5rem 1rem;
  margin: 0 0.5rem;
  border-radius: ${(props) => props.theme.borderRadius.md || '0.375rem'};
  font-size: ${(props) => props.theme.typography.fontSize.sm || '0.875rem'};
  font-weight: ${(props) => props.theme.typography.fontWeight.medium || '500'};
  border: none;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    transform: translateY(-2px);
  }
`;

const MockPaymentBtns = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  width: 100%;
  justify-content: center;

  .mock-success {
    background: #2afd9b;
    color: #1e293b;
  }
  .mock-failure {
    background: #f8343d;
    color: white;
  }
`;

const ArrowIcon = styled.img`
  height: 16px;
  width: 16px;
  margin-left: auto;
`;
